import {t} from '@lingui/core/macro';

import {ITariff} from 'entities/tariff';

export type TariffAdvantageKey = keyof Pick<ITariff,
    'monitor_ssl_enabled'
    | 'monitor_domain_watch_enabled'
    | 'monitor_ssl_redirect_watch_enabled'
    | 'monitor_frequent_checking'
    | 'monitor_parse_errors'
    | 'monitor_content_enabled'
    | 'status_pages_max_count_limit'
    | 'reports_max_count_limit'
    | 'server_max_count_limit'
    | 'port_monitor_max_count_limit'
    | 'location_max_count_limit'
    | 'monitor_extended_methods'
    | 'monitor_screenshot_on_error'
    | 'monitor_robots_txt_watch_enabled'
    | 'monitor_http_basic_auth_enabled'
    >;

export type TariffAdvantageSource = Pick<ITariff, TariffAdvantageKey>;

export interface ITariffAdvantage {
    key: TariffAdvantageKey,
    label: string,
}

export interface ITariffAdvantageState extends ITariffAdvantage {
    included: boolean,
}

export const getTariffAdvantages = (): ITariffAdvantage[] => [
    {key: 'monitor_ssl_enabled', label: t`Мониторинг SSL`},
    {key: 'monitor_domain_watch_enabled', label: t`Мониторинг регистрации домена`},
    {key: 'monitor_ssl_redirect_watch_enabled', label: t`Проверка редиректа с http на https`},
    {key: 'monitor_frequent_checking', label: t`Учащенный мониторинг`},
    {key: 'monitor_parse_errors', label: t`Разбор проблем`},
    {key: 'monitor_content_enabled', label: t`Проверка контента страницы`},
    {key: 'status_pages_max_count_limit', label: t`Публичные страницы статуса`},
    {key: 'reports_max_count_limit', label: t`Отчёты о доступности`},
    {key: 'server_max_count_limit', label: t`Мониторинг серверов`},
    {key: 'port_monitor_max_count_limit', label: t`Мониторинг портов`},
    {key: 'location_max_count_limit', label: t`Проверки из разных гео-локаций`},
    {key: 'monitor_extended_methods', label: t`Расширенный мониторинг`},
    {key: 'monitor_screenshot_on_error', label: t`Скриншот страницы ошибки`},
    {key: 'monitor_robots_txt_watch_enabled', label: t`Проверка robots.txt`},
    {key: 'monitor_http_basic_auth_enabled', label: t`HTTP Basic Auth`},
];

export const hasTariffAdvantage = (
    tariff: TariffAdvantageSource | null | undefined,
    key: TariffAdvantageKey,
): boolean => {
    const value = tariff?.[key];

    return typeof value === 'number' ? value > 0 : !!value;
};

export const pickTariffAdvantages = (
    tariffs: ITariff[],
    userTariff?: TariffAdvantageSource | null,
): ITariffAdvantageState[] => {
    const catalog = getTariffAdvantages();
    const included = catalog.filter(({key}) => hasTariffAdvantage(userTariff, key));
    const missing = catalog.filter(({key}) => !hasTariffAdvantage(userTariff, key));
    const available = tariffs.filter((tariff) => !tariff.is_archive);
    const sellable = missing.filter(({key}) => available.some((tariff) => hasTariffAdvantage(tariff, key)));

    return [
        ...(sellable.length ? sellable : missing).map((advantage) => ({...advantage, included: false})),
        ...included.map((advantage) => ({...advantage, included: true})),
    ];
};

export const getOfferedAdvantages = (advantages: ITariffAdvantageState[]): ITariffAdvantageState[] =>
    advantages.filter(({included}) => !included);

export const getIncludedAdvantages = (advantages: ITariffAdvantageState[]): ITariffAdvantageState[] =>
    advantages.filter(({included}) => included);

export const getAdvantagesMinPrice = (
    tariffs: ITariff[],
    advantages: ITariffAdvantage[],
): number | null => {
    const prices = tariffs
        .filter((tariff) => !tariff.is_archive && tariff.price > 0)
        .filter((tariff) => advantages.some(({key}) => hasTariffAdvantage(tariff, key)))
        .map(({price}) => price);

    return prices.length ? Math.min(...prices) : null;
};
